// Pantalla `command="login"`. Port directo de la rama `when "login"` de
// keycloak.coffee, manteniendo todos los condicionales: detección
// nativeApp, hasRegisterFeature según client_id y nativeApp, autoFocus
// según data.form.email, Enter-en-email salta a password, link "He
// olvidado mi contraseña" con getFinalURL, botón submit type=button +
// onClick programmatic submit en iOS para evitar conflicto TouchID con
// universal links, social login filtrado, link a registro con
// hint_email + nativeApp, botón Cancelar para AIA del account-console.

import { useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import type { KcData } from "../kcData";
import { Input } from "../components/Input";
import { SocialLogins } from "../components/SocialLogins";
import { StandardPage } from "../components/StandardPage";
import { msg } from "../i18n";
import { getAuthRequestFlags } from "../authRequestFlags";
import {
  getFinalURL,
  isIOSNativeApp,
  NATIVE_APPS_WITH_REGISTER_FEATURE,
} from "../nativeApp";
import { getFdPublicConfig } from "../window";

export interface LoginPageProps {
  data: KcData;
  running: boolean;
  nativeApp: string | null;
  onUserAction: () => void;
  onChange: (key: string, value: string | boolean) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>, iosLoginSubmit?: boolean) => void;
}

export function LoginPage(props: LoginPageProps) {
  const { data, running, nativeApp, onUserAction, onChange, onSubmit } = props;
  const passwordRef = useRef<HTMLInputElement | null>(null);

  // readOnly-until-focus para bloquear autofill del browser sin restringir
  // edición. Si el email viene pre-rellenado en el primer render — porque
  // el cliente envió login_hint, o porque venimos de un error de validación
  // — el campo arranca readOnly. Chrome (y otros) NO autofillan campos
  // readOnly, así que el value pre-rellenado sobrevive el ciclo de
  // autofill que ocurre justo tras cargar la página. En cuanto el usuario
  // toca el campo (focus por click o tab), retiramos el readOnly y el
  // input es completamente editable. El usuario sólo paga un click extra
  // si quiere cambiar el email — la misma fricción que cualquier
  // formulario. Sin esto, el value controlado por React (formEmail = ramiro)
  // se sobrescribe con la cuenta más reciente guardada por el password
  // manager (p.ej. alex), bug del que se escapaba sin pista visual.
  const [emailReadOnly, setEmailReadOnly] = useState(
    typeof data.form?.email === "string" && data.form.email !== "",
  );

  // Checkbox "Mantener la sesión iniciada" como estado controlado: además
  // de viajar en el POST del form (name=rememberMe), su valor se propaga
  // al botón de Google (SocialLogins añade remember_me=on|off al link del
  // broker y FdGoogleIdentityProvider lo traduce a la auth note estándar).
  // Arranca marcado si login.rememberMe viene del server (cookie
  // KEYCLOAK_REMEMBER_ME de un login recordado anterior).
  const [rememberMe, setRememberMe] = useState(!!data.form?.rememberMe);

  // hasRegisterFeature: la app ve el botón de registro si:
  //   - no hay nativeApp, o
  //   - el nativeApp pertenece a la lista whitelist
  // Y además, los clientes facturadirecta-capture / facturadirecta-api
  // nunca lo ven (son flujos máquina-a-máquina).
  let hasRegisterFeature =
    !nativeApp ||
    (NATIVE_APPS_WITH_REGISTER_FEATURE as readonly string[]).includes(nativeApp);
  if (
    hasRegisterFeature &&
    (data.clientId === "facturadirecta-capture" ||
      data.clientId === "facturadirecta-api")
  ) {
    hasRegisterFeature = false;
  }

  // La casilla remember-me sólo se ofrece en logins "normales" (ver
  // QUERYSTRING_BEHAVIOR.md §11). Se oculta cuando:
  //   - nativeApp: la webview nativa gestiona su persistencia con refresh
  //     tokens, la casilla no aplica;
  //   - popup (window.opener): el reauth de fd3 (GlobalReauthHandler) va
  //     siempre por signinPopup — es una confirmación de identidad, no un
  //     login nuevo; misma detección que PopupAwareBackButton;
  //   - kc_action en el auth request (AIA: p.ej. "Actualizar contraseña"
  //     desde el account console): Keycloak interpone un login de
  //     confirmación que CONSERVA la sesión existente con su flag
  //     rememberMe original — la casilla no tendría efecto;
  //   - prompt=login: reautenticación forzada por el cliente (redundante
  //     con el popup para fd3, cubre reauth full-page de otros clientes);
  //   - scope offline_access: autorización de integración/API, el offline
  //     token no depende de la sesión SSO del navegador;
  //   - clientId facturadirecta-api: nuestro ForcePromptAuthenticator le
  //     fuerza prompt=login+consent SERVER-SIDE, así que la URL no delata
  //     la reauth y hay que ir por cliente.
  // En todos esos casos tampoco se propaga remember_me al botón de Google
  // (SocialLogins recibe undefined y no añade el parámetro).
  const authFlags = getAuthRequestFlags(window.location.search, data);
  const isPopup =
    typeof window !== "undefined" &&
    window.opener != null &&
    !window.opener.closed;
  const showRememberMe =
    data.realm.rememberMe &&
    !nativeApp &&
    !isPopup &&
    !authFlags.kcAction &&
    !authFlags.promptLogin &&
    !authFlags.offlineAccess &&
    data.clientId !== "facturadirecta-api";

  const url = data.url!;
  const formEmail = (data.form?.email as string | undefined) ?? "";
  const formPassword = (data.form?.password as string | undefined) ?? "";

  const handleEmailKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    onUserAction();
    if (e.which === 13) {
      // Si ya hay email, Enter pasa al campo password (no submit).
      if (formEmail !== "") {
        passwordRef.current?.focus();
        e.preventDefault();
      }
    }
  };

  const handlePasswordKeyPress = (_e: KeyboardEvent<HTMLInputElement>) => {
    onUserAction();
  };

  const isIOS = isIOSNativeApp(nativeApp);

  // En la app nativa de iOS cambiamos el botón de submit a button para
  // evitar el autosubmit de TouchID que da problemas con los universal
  // links. El submit lo disparamos manualmente desde onClick con un
  // setTimeout 1ms — el delay es para que el setState del onUserAction
  // tenga tiempo de fijar los datos antes del submit.
  const handleIOSClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onUserAction();
    if (isIOS) {
      const ev = e as unknown as FormEvent<HTMLFormElement>;
      setTimeout(() => onSubmit(ev, true), 1);
    }
  };

  return (
    <StandardPage page="login" data={data}>
      <form
        id="kc-form-login"
        action={url.loginAction}
        method="post"
        onSubmit={onSubmit}
      >
        <Input
          type="email"
          id="username"
          name="username"
          autoComplete="username"
          autoFocus={!formEmail}
          value={formEmail}
          readOnly={emailReadOnly}
          onFocus={() => {
            if (emailReadOnly) setEmailReadOnly(false);
          }}
          onChange={(e) => onChange("email", e.target.value)}
          onKeyPress={handleEmailKeyPress}
          placeholder={msg("email")}
          messagesPerField={data.messagesPerField}
        />
        <Input
          type="password"
          id="password"
          name="password"
          autoComplete="current-password"
          inputRef={(el) => {
            passwordRef.current = el;
          }}
          autoFocus={!!formEmail}
          value={formPassword}
          onChange={(e) => onChange("password", e.target.value)}
          onKeyPress={handlePasswordKeyPress}
          placeholder={msg("password")}
          messagesPerField={data.messagesPerField}
        />
        {showRememberMe ? (
          <div className="remember">
            <label className="checkboxRemember">
              <input
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="customCheckbox" />
              {msg("rememberMe")}
            </label>
            <p className="rememberHelp">{msg("rememberMeHelp")}</p>
          </div>
        ) : null}
        <p className="note">
          <a tabIndex={6} href={getFinalURL(url.resetCredentialsUrl, nativeApp)}>
            {msg("doForgotPassword")}
          </a>
        </p>
        <button
          type={isIOS ? "button" : "submit"}
          className="btn btn-lg btn-block btn-primary"
          disabled={running}
          onClick={handleIOSClick}
        >
          {msg("doLogIn")}
        </button>
        {/*
          Cancelar de la AIA disparada por account-console (típicamente
          UPDATE_PASSWORD que requiere reauth, o el flow inicial cuando
          el usuario abre /account sin sesión válida). NO podemos usar
          cancel-aia aquí porque la AIA aún no está en execution.

          Linkamos al BASE_URL del app principal (de fdPublicConfig),
          NO a clientBaseUrl (que es el del account-console). Razón:
          si el account-console todavía no tiene sesión válida (caso
          típico cuando el SSO de KC se ha ido pero la sesión del app
          principal sigue), entrar al account-console redirige otra
          vez a este mismo login → bucle. BASE_URL del app principal
          es la salida natural del flow de auth para el usuario, y si
          su sesión del app sigue válida cae directo en su dashboard. */}
        {data.clientId === "account-console" ? (
          <a
            href={getFdPublicConfig().BASE_URL}
            className="btn btn-lg btn-block btn-default"
          >
            {msg("doCancel")}
          </a>
        ) : null}
        {data.socialProviders ? (
          <SocialLogins
            socialProviders={data.socialProviders}
            nativeApp={nativeApp}
            rememberMe={showRememberMe ? rememberMe : undefined}
          />
        ) : null}
        {hasRegisterFeature ? <h3>{msg("noAccount")}</h3> : null}
        {hasRegisterFeature ? (
          <a
            className="btn btn-lg btn-block btn-success"
            href={
              url.registrationUrl +
              (formEmail
                ? "&hint_email=" + encodeURIComponent(formEmail)
                : "") +
              (nativeApp ? `&nativeApp=${nativeApp}` : "")
            }
            id="fd-registration-link"
          >
            {msg("doRegister")}
          </a>
        ) : null}
      </form>
    </StandardPage>
  );
}
